import { useCallback } from 'react';

import type { ActionToExecute } from '$src/lib/extensions/types';

import { useAddLineItems } from '$src/hooks/mutations/useAddLineItems';

export const useActionExecutor = () => {
  const { mutateAsync: addLineItems } = useAddLineItems();

  /**
   * Execute a list of actions - ultra simplified
   */
  const executeActions = useCallback(
    async (
      actions: ActionToExecute[],
    ): Promise<{
      customAttributes: Array<{ key: string; value: string }>;
      addedProducts: Array<{ variantId: string; quantity: number; lineItemId?: string }>;
    }> => {
      const customAttributes: Array<{ key: string; value: string }> = [];
      const addedProducts: Array<{ variantId: string; quantity: number; lineItemId?: string }> = [];

      console.warn('[ActionExecutor] Executing actions:', actions);

      for (const action of actions) {
        try {
          switch (action.type) {
            case 'add-order-custom-attribute':
              customAttributes.push({
                key: action.data.key!,
                value: action.data.value!,
              });
              console.warn('[ActionExecutor] Added custom attribute:', action.data);
              break;

            case 'add-product': {
              if (!action.data.variantId || !action.data.quantity) {
                console.error('[ActionExecutor] Missing variantId or quantity for add-product:', action);
                break;
              }

              // Use custom attributes from config, or fallback to default tracking attributes
              const customAttributes =
                action.data.customAttributes && action.data.customAttributes.length > 0
                  ? action.data.customAttributes
                  : [];

              const addResult = await addLineItems({
                lineItems: [
                  {
                    variantId: action.data.variantId,
                    quantity: action.data.quantity,
                    customAttributes,
                  },
                ],
              });

              // Try to find the newly added line item ID
              // This is a best-effort approach
              const newLineItem = addResult.data.lineItems?.find(
                (item) => item.variantId === action.data.variantId,
              );

              addedProducts.push({
                variantId: action.data.variantId!,
                quantity: action.data.quantity!,
                lineItemId: newLineItem?.id,
              });
              console.warn('[ActionExecutor] Added product:', {
                ...action.data,
                lineItemId: newLineItem?.id,
              });
              break;
            }

            default:
              console.warn('[ActionExecutor] Unknown action type:', action.type);
          }
        } catch (error) {
          console.error('[ActionExecutor] Failed to execute action:', action, error);
          // Continue with other actions even if one fails
        }
      }

      return {
        addedProducts,
        customAttributes,
      };
    },
    [addLineItems],
  );

  return {
    executeActions,
  };
};
